import { createSlice } from '@reduxjs/toolkit';
import { setCheckoutLoader } from '../loader/loaderSlice';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { getPollingID, getSubscriptionID, sendStripeReq, subscribeToPriceChange } from './fetch';
import { calcPercent } from '../../helper/utils';
import { setEventData, setEventDataList } from '../events/eventsSlice';
import { setThreeDS } from '../signup/signupSlice';

export const checkoutSlice = createSlice({
    name: 'checkout',
    initialState: {
        cardholderName: '',
        token: null,
        isSuccess: null,
        isFormValid: null,
        id: null,
        regularPrice: null,
        stripeNonce: '',
        showPaymentError: false,
        errorText: '',
        isCardFormShown: false,
    },
    reducers: {
        setCardholderName: (state, action) => {
            state.cardholderName = action.payload;
        },
        setRequestResult: (state, action) => {
            const {
              isSuccess,
              id,
              finalPrice,
              regularPrice,
            } = action.payload;

            state.isSuccess = isSuccess;
            state.id = id;
            state.finalPrice = finalPrice;
            state.regularPrice = regularPrice;
        },
        setDiscountPercentage: (state, action) => {
            const {finalPrice, regularPrice} = action.payload;
            state.discountPercentage = calcPercent((regularPrice - finalPrice), regularPrice);
        },
        setFormValidity: (state, action) => {
            state.isFormValid = action.payload;
        },
        setStripeNonce: (state, action) => {
            state.stripeNonce = action.payload;
        },
        setShowPaymentError: (state, action) => {
            const {show,text} = action.payload;
            state.showPaymentError = show;
            state.errorText = text;
        },
        setIfCardFormShown: (state, action) => {
          state.isCardFormShown = action.payload;
        },
    },
});

export const {
    setCardholderName,
    setStripeNonce,
    setRequestResult,
    setDiscountPercentage,
    setFormValidity,
    setShowPaymentError,
    setIfCardFormShown,
} = checkoutSlice.actions;


const sendServerReq = async (stripe, paymentMethodObj, plan, token, analyticsParams, eventsData, onError, onSuccess, payment_intent_id) => {
    const {stripeNonce, methodName} = paymentMethodObj;
    const pollingID = await getPollingID(stripeNonce, plan, token, analyticsParams, eventsData, onError, payment_intent_id);
    const purchaseRequest = await subscribeToPriceChange(pollingID);
    const { status, errors, data } = purchaseRequest;

    if (status) {
        if (status === 'requires_action' && data && data.payment_intent_secret) {
            const cardPaymentSecret = data.payment_intent_secret;
            const cardPaymentData = methodName ? {payment_method: stripeNonce} : {setup_future_usage: 'off_session'};
            const cardPaymentOptions = methodName ? {handleActions: false} : {};

            const {error: errorAction, paymentIntent} = await stripe.confirmCardPayment(cardPaymentSecret,cardPaymentData,cardPaymentOptions);

            if (errorAction) {
                if (methodName) {
                    onError(errorAction.message, data);
                } else {
                    await sendServerReq(stripe, paymentMethodObj, plan, token, analyticsParams, eventsData, onError, onSuccess, data.payment_intent_id);
                }
                return;
            } else {
                if (methodName) {
                    if (paymentIntent.status === 'requires_action') {
                        const {error} = await stripe.confirmCardPayment(data.payment_intent_secret);
                        if (error) {
                            onError(error, data);
                            return
                        }
                    }

                    onSuccess();
                }
            }

            await sendServerReq(stripe, paymentMethodObj, plan, token, analyticsParams, eventsData, onError, onSuccess, paymentIntent.id);

        } else {
            const { subscription_id, price_with_discount, regular_price, upsell_type, event_id } = purchaseRequest;

            return onSuccess(subscription_id, price_with_discount, regular_price, upsell_type, event_id);
        }
    }

    if (errors) {
        if (status === 'requires_action') return;

        onError(errors.messages || 'An error has occurred', data);
    }
};

export const sendMainPurchaseRequest = (paymentMethodObj = null, stripe, card, name, plan, token, analyticsParams, eventsData, successPayment, failedPayment) => async dispatch => {
    const onError = (data, errorData) => {
        if (failedPayment) failedPayment();

        let txt = data;
        if (typeof txt !== 'string') txt = data.message;
        if (typeof txt !== 'string') {
            txt = 'Payment error';
            console.log(data);
        }

        dispatch(setEventData({id: 'declineCode', data: errorData?.decline_code || errorData?.processor_response,}));
        sendAnalyticsEvents(EVENTS.onboardingPaymentDecline, {...eventsData, declineCode: errorData?.decline_code || errorData?.processor_response,});
        dispatch(setShowPaymentError({show: true, text: txt}));
    };
    const onSuccess = (id, finalPrice, regularPrice, upsell_type, event_id) => {
        if (successPayment) successPayment(id, event_id, finalPrice);
        if (finalPrice) {
            dispatch(setDiscountPercentage({finalPrice, regularPrice}));
        }

        sendAnalyticsEvents(EVENTS.subscribed, eventsData);

        dispatch(setRequestResult({
            isSuccess: true,
            id,
            finalPrice,
            regularPrice,
            upsellType: upsell_type
        }));
    };

    dispatch(setCheckoutLoader({show: true, type: 'checkout'}));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, eventsData);

    try {
        if (paymentMethodObj) {
            await sendServerReq(stripe, paymentMethodObj, plan, token, analyticsParams, eventsData, onError, onSuccess, '');
        } else {
            const { paymentMethod, error } = await sendStripeReq(stripe, card, name);
            let stripeNonce = paymentMethod?.id || error?.code;

            if (error) {
                console.error('Failed to make a payment: ', error.message);
                dispatch(setShowPaymentError({ show: true, text: error.message }));
            } else {
                dispatch(setStripeNonce(stripeNonce));
                await sendServerReq(stripe, { stripeNonce }, plan, token, analyticsParams, eventsData, onError, onSuccess, '');
            }
        }
    } finally {
        dispatch(setCheckoutLoader({ show: false, type: '' }));
    }
};

export const braintreePurchase = (paypalNonce, binData, threeDSInstance, plan, token, analyticsParams, eventsData, forceThreeDS, successPayment) => async dispatch => {
  const onError = (data, errorData) => {
    let txt = data;
    if (typeof txt !== 'string') txt = data.message;
    if (typeof txt !== 'string') {
      txt = 'Payment error';
    }

    dispatch(setEventData({id: 'decline_code', data: errorData?.decline_code || errorData?.processor_response,}));
    sendAnalyticsEvents(EVENTS.onboardingPaymentDecline, {...eventsData, declineCode: errorData?.decline_code || errorData?.processor_response,});
    dispatch(setShowPaymentError({ show: true, text: txt }));
    dispatch(setCheckoutLoader({show: false, type: ''}));
  };
  const onSuccess = ({ customer_id, subscription_id, event_id, price_with_discount }) => {
    if (successPayment) successPayment(subscription_id, event_id, price_with_discount);
    const localData = {
      customer_id,
      subscription_id,
      plan_id: plan,
    };
    dispatch(setEventDataList(localData));
    sendAnalyticsEvents(EVENTS.subscribed, {...eventsData, ...localData});
    dispatch(setRequestResult({isSuccess: true, id: subscription_id}));
    dispatch(setCheckoutLoader({show: false, type: ''}));
  };

  const verifyWithThreeDS = (nonce, amount, data) => {
    threeDSInstance.verifyCard({
      nonce: nonce,
      bin: binData,
      amount: amount.toString(),
    }, (verifyError, payload) => {
      if (verifyError) {
        console.error('verify error: ', verifyError);
        onError(verifyError, data);
        return;
      }
      // console.log('verify: ', payload);

      dispatch(
        braintreePurchase(
          payload.nonce,
          binData,
          threeDSInstance,
          plan,
          token,
          analyticsParams,
          eventsData,
          successPayment,
        )
      );
    });
  };

  if (forceThreeDS && forceThreeDS !== 0) {
    if (forceThreeDS === 1) dispatch(setThreeDS(0));
    verifyWithThreeDS(paypalNonce, eventsData?.periodPrice);
  } else {
    const purchaseRequest = await getSubscriptionID(paypalNonce, plan, token, analyticsParams, eventsData);

    try {
      const { errors, data } = purchaseRequest;
      if (errors) {
        if (errors?.messages === 'User action required') {
          const { payment_method_nonce, validation_amount } = data;

          verifyWithThreeDS(payment_method_nonce, validation_amount, data);
          return;
        }

        onError(errors?.messages || 'An error has occurred', data);
        return;
      }
      onSuccess(data);
    } catch (err) {
      onError(err);
    }
  }
};


export const selectSuccess = state => state.checkout.isSuccess;
export const selectName = state => state.checkout.cardholderName;
export const selectFormValidity = state => state.checkout.isFormValid;
export const selectID = state => state.checkout.id;
export const selectShowPaymentError = state => state.checkout.showPaymentError;
export const selectErrorText = state => state.checkout.errorText;
export const selectIfCardFormShown = (state) => state.checkout.isCardFormShown;

export default checkoutSlice.reducer;
